import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Eye } from 'lucide-react';
import { usePropertyData } from '../../../../hooks/usePropertyData';
import PreviewLinkSection from '../sections/PreviewLinkSection';

interface PublishingTabProps {
  propertyId?: string;
}

const PublishingTab: React.FC<PublishingTabProps> = ({ propertyId }) => {
  const { register, watch } = useFormContext();
  const { propertyStatuses, loading } = usePropertyData();
  const status_id = watch('status_id');

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Opzioni di Pubblicazione</h3>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stato Pubblicazione*
          </label>
          <select
            {...register('status_id', { required: 'Lo stato è obbligatorio' })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Seleziona stato...</option>
            {propertyStatuses.map(status => (
              <option key={status.id} value={status.id}>
                {status.name === 'published' ? '✅ Pubblicato' :
                 status.name === 'draft' ? '📝 Bozza' :
                 status.name === 'archived' ? '📦 Archiviato' : status.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              {...register('featured')}
              className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <span className="ml-2 text-sm text-gray-700">In Evidenza</span>
          </label>
        </div>

        {/* Sezione portali immobiliari */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-700 mb-4">Portali Immobiliari</h4>
          
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('publish_immobiliare')}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Immobiliare.it</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('publish_wikicasa')}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Wikicasa</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('publish_idealista')}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Idealista</span>
            </label>
          </div>
        </div>

        {/* Sezione ordinamento */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-700 mb-4">Ordinamento</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priorità (0-99)
              </label>
              <input
                type="number"
                {...register('priority')}
                min="0"
                max="99"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
              />
              <p className="mt-1 text-xs text-gray-500">
                Valori più bassi = priorità più alta. Gli immobili con priorità più alta verranno visualizzati per primi.
              </p>
            </div>
          </div>
        </div>

        {/* Link privato per il cliente */}
        <PreviewLinkSection propertyId={propertyId} />

        {/* Sezione Anteprima */}
        {status_id === propertyStatuses.find(s => s.name === 'published')?.id && watch('id') && (
          <div className="pt-4 border-t">
            <a
              href={`/immobile/${watch('id')}`}
              target="_blank"
              rel="noopener noreferrer" 
              className="w-full bg-secondary hover:bg-secondary-light text-white rounded-lg py-3 transition-colors flex items-center justify-center"
            >
              <Eye className="h-5 w-5 mr-2" />
              Anteprima
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishingTab;